import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getHeroContent, StoreLogo, CartBtn, TabContent, StoreFooter, AWS_URL, FaHeart, InlineEditableImage
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component for proper editing and saving
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  // Get the current value from storeTheme first, then from props
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  // Update local value when storeTheme changes or when editing starts
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    // Save the current local value to parent
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    // Deactivate edit mode
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      // Then navigate if provided and not in modal
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    // Save on blur (when clicking outside)
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#c9a96e] focus:ring-2 focus:ring-[#c9a96e]/20 dark:border-slate-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("fullstory") || field.toLowerCase().includes("story");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  // Display the current value - force re-render when storeTheme changes
  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function RoyalCoutureLayout({ p }) {
  const T = p.storeTheme || {};
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Force re-render when storeTheme changes
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    // This will trigger re-render when storeTheme updates
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);
  
  const products = (p.filteredProducts || []).slice(0, 6);
  
  // Refs for sections
  const homeRef = useRef(null);
  const atelierRef = useRef(null);
  const galleryRef = useRef(null);
  const collectionRef = useRef(null);
  const editorialRef = useRef(null);
  const heritageRef = useRef(null);
  
  // YOUR SELECTED IMAGE - Beautiful gown model
  const gownModelImageConfig = {
    field: "heroImage",
    src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1bd2afa0-3726-40e0-aed3-d16efe336b5c_14.avif",
  };
  const backupImage = "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/69915d5a-68e0-458d-a75b-deef3a61de79_15.avif";
  
  // Navigate to section - normal navigation
  const scrollToSection = useCallback((ref, sectionName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Don't navigate if we're actively editing
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActiveSection(sectionName);
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMobileMenuOpen(false);
  }, [p.previewEditMode, p.activeEditField]);
  
  // Handle navigation after Enter key in edit mode
  const handleNavigateAfterEdit = useCallback((sectionName) => {
    setActiveSection(sectionName);
    setMobileMenuOpen(false);
    const refMap = {
      home: homeRef,
      atelier: atelierRef,
      collection: collectionRef,
      editorial: editorialRef,
      heritage: heritageRef,
    };
    const targetRef = refMap[sectionName];
    if (targetRef && targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);
  
  // Open blog modal
  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);
  
  // Close blog modal
  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);
  
  // Handle backdrop click
  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedPost(null);
    }
  }, [p.previewEditMode, p.activeEditField]);
  
  // Preload and animate image
  useEffect(() => {
    const img = new Image();
    const heroImage = p.storeTheme?.heroImage || gownModelImageConfig.src;
    img.src = heroImage;
    img.onload = () => {
      setTimeout(() => setImageLoaded(true), 200);
    };
    img.onerror = () => {
      const backup = new Image();
      backup.src = backupImage;
      backup.onload = () => {
        setTimeout(() => setImageLoaded(true), 200);
      };
    };
  }, [p.storeTheme?.heroImage]);
  
  // Gentle floating animation for gown cards
  useEffect(() => {
    const timers = [];
    products.forEach((_, index) => {
      const timer = setTimeout(() => {
        setVisibleCards(prev => [...prev, index]);
      }, index * 180);
      timers.push(timer);
    });
    return () => timers.forEach(clearTimeout);
  }, [products]);
  
  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: "home", ref: homeRef },
        { id: "atelier", ref: atelierRef },
        { id: "gallery", ref: galleryRef },
        { id: "collection", ref: collectionRef },
        { id: "editorial", ref: editorialRef },
        { id: "heritage", ref: heritageRef },
      ];
      
      let currentSection = "home";
      for (const section of sections) {
        if (section.ref.current) {
          const rect = section.ref.current.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            currentSection = section.id;
            break;
          }
        }
      }
      setActiveSection(currentSection);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  const copy = getHeroContent(p, {
    eyebrow: "MAISON DE COUTURE",
    title: "Where Gowns Become Dreams",
    subtitle: "Step into our atelier where every gown tells a story of timeless elegance and unparalleled craftsmanship.",
    primaryCta: "Discover Gowns",
    secondaryCta: "Our Legacy",
  });
  
  // Gentle floating animation for cards
  const getFloatingDelay = (index) => {
    const delays = [0, 0.15, 0.3, 0.45, 0.6, 0.75];
    return delays[index % delays.length];
  };
  
  // Editorial posts with editable fields - NOW WITH imageField
  const editorialPosts = [
    {
      id: 1,
      titleKey: "editorial1_title",
      title: "The Modern Silhouette",
      descKey: "editorial1_desc",
      desc: "Discover how contemporary design meets timeless elegance in our latest editorial feature. Each piece tells a unique story of craftsmanship and vision.",
      fullContentKey: "editorial1_content",
      fullContent: "The modern silhouette is a celebration of both form and freedom. Our latest collection embraces the natural curves of the body while adding structural elements that create visual interest. From flowing capes to sculpted bodices, each piece is designed to empower the woman who wears it.",
      imageField: "editorial1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/921ac618-3edb-4b1e-af5b-1409251f7bc7_17.avif",
      dateKey: "editorial1_date",
      date: "March 15, 2024",
      authorKey: "editorial1_author",
      author: "Isabella Rossi"
    },
    {
      id: 2,
      titleKey: "editorial2_title",
      title: "The Art of Embroidery",
      descKey: "editorial2_desc",
      desc: "Behind the scenes look at the intricate embroidery that defines our couture pieces.",
      fullContentKey: "editorial2_content",
      fullContent: "Embroidery is the soul of couture. Our master embroiderers spend hundreds of hours hand-stitching each gown, using techniques passed down through generations. From delicate floral motifs to bold geometric patterns, every stitch tells a story.",
      imageField: "editorial2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/250f7d34-c815-460a-ae1d-413720d86ba7_18.webp",
      dateKey: "editorial2_date",
      date: "March 10, 2024",
      authorKey: "editorial2_author",
      author: "Marcus Chen"
    },
    {
      id: 3,
      titleKey: "editorial3_title",
      title: "A Day in the Atelier",
      descKey: "editorial3_desc",
      desc: "Experience the magic of creation as we take you through a day in our Parisian atelier.",
      fullContentKey: "editorial3_content",
      fullContent: "The atelier comes alive each morning with the sound of sewing machines and gentle conversation. Our artisans arrive early, each dedicated to their craft. From pattern cutting to final fitting, witness the transformation of fabric into art.",
      imageField: "editorial3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1206751b-faac-44b8-baae-56b1ace9a191_19.jpg",
      dateKey: "editorial3_date",
      date: "March 5, 2024",
      authorKey: "editorial3_author",
      author: "Elena Vasquez"
    }
  ];

  // Get editable editorial posts with imageField
  const editableEditorialPosts = editorialPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Atelier images with editable fields
  const atelierImageConfig = {
    field: "atelierImage",
    src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/4f8ffada-b85c-4f25-978f-51189f3d6006_20.webp",
  };

  const heritageImageConfig = {
    field: "heritageImage",
    src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/4b45ac8c-b669-41c7-b0fe-3fee47743550_22.jpg",
  };

  const editorialSectionImageConfig = {
    field: "editorialSectionImage",
    src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/69de6f53-407b-4273-b7cd-a503c6406c26_21.jpg",
  };
  
  // Top bar messages
  const topBarMessages = [
    { key: "top_bar_message_1", value: "BESPOKE GOWNS" },
    { key: "top_bar_message_2", value: "ATELIER EXCLUSIVE" },
    { key: "top_bar_message_3", value: "COMPLIMENTARY FITTING" },
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", section: "home" },
    { key: "nav_atelier", label: "ATELIER", section: "atelier" },
    { key: "nav_collection", label: "COLLECTION", section: "collection" },
    { key: "nav_editorial", label: "EDITORIAL", section: "editorial" },
    { key: "nav_heritage", label: "HERITAGE", section: "heritage" },
  ];

  // Category items
  const categoryItems = [
    { key: "category_ball_gowns", name: "Ball Gowns", icon: "👑", descKey: "category_ball_gowns_desc", desc: "Grand & Regal" },
    { key: "category_aline_gowns", name: "A-Line Gowns", icon: "✨", descKey: "category_aline_gowns_desc", desc: "Classic Elegance" },
    { key: "category_mermaid_gowns", name: "Mermaid Gowns", icon: "🧜‍♀️", descKey: "category_mermaid_gowns_desc", desc: "Sculpted Silhouette" },
    { key: "category_sheath_gowns", name: "Sheath Gowns", icon: "💫", descKey: "category_sheath_gowns_desc", desc: "Modern Sophistication" },
  ];

  // Tab items
  const tabItems = [
    { key: "products", labelKey: "tab_products_label", label: "THE COLLECTION" },
    { key: "services", labelKey: "tab_services_label", label: "ATELIER SERVICES" },
    { key: "details", labelKey: "tab_details_label", label: "THE HOUSE" },
  ];
  
  return (
    <div key={refreshKey} className="min-h-screen bg-[#0a0a1a]" style={{ fontFamily: T.fontFamily || "'Playfair Display', serif" }}>
      
      {/* Elegant Top Bar - All messages editable */}
      <div className="relative bg-[#0a0a1a] text-[#c9a96e] py-3 border-b border-[#c9a96e]/15 overflow-hidden z-20">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#c9a96e]/8 to-transparent animate-shimmer-slow pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 text-center text-xs tracking-[0.35em] uppercase font-light">
          ✦ {topBarMessages.map((msg, index) => (
            <React.Fragment key={msg.key}>
              <InlineEditableTextWithNav 
                p={p} 
                field={msg.key} 
                value={msg.value}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
              {index < topBarMessages.length - 1 && " ✦ "}
            </React.Fragment>
          ))} ✦
        </div>
      </div>
      
      {/* Transparent Navigation with Working Links */}
      <nav className="sticky top-0 z-50 bg-[#0a0a1a]/80 backdrop-blur-xl border-b border-[#c9a96e]/15">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={(e) => scrollToSection(homeRef, "home", e)}
          >
            <StoreLogo storeInfo={p.storeInfo} border="border border-[#c9a96e]" />
            <div>
              <span className="font-light text-xl tracking-wide text-white">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="businessName" 
                  value={p.storeInfo?.businessName || "Royal Couture"} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </span>
              <p className="text-[9px] text-[#c9a96e] tracking-[0.4em] uppercase mt-0.5">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="business_subtitle" 
                  value="COUTURE HOUSE" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </p>
            </div>
          </div>
          
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex gap-10">
            {navItems.map((item) => {
              const refMap = {
                home: homeRef,
                atelier: atelierRef,
                collection: collectionRef,
                editorial: editorialRef,
                heritage: heritageRef,
              };
              return (
                <button 
                  key={item.key}
                  onClick={(e) => scrollToSection(refMap[item.section], item.section, e)}
                  className={`text-sm tracking-wide transition-all duration-300 relative group ${activeSection === item.section ? "text-[#c9a96e]" : "text-white/60 hover:text-[#c9a96e]"}`}
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={item.key} 
                    value={item.label} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage={item.section} 
                  />
                  <span className={`absolute -bottom-1 left-1/2 w-0 h-px bg-[#c9a96e] transition-all duration-300 group-hover:w-full group-hover:left-0 ${activeSection === item.section ? "w-full left-0" : ""}`} />
                </button>
              );
            })}
          </div>
          
          <div className="flex gap-6">
            <button onClick={p.handleGoToWishlist} className="relative group">
              <FaHeart className="text-white/50 text-xl group-hover:text-[#c9a96e] transition-all duration-300" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#c9a96e] text-[#0a0a1a] text-[10px] flex items-center justify-center font-bold">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark />
            {p.userMenuElement}
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#0a0a1a] border-b border-[#c9a96e]/15 py-4 px-6 flex flex-col gap-2 z-50">
            {navItems.map((item) => {
              const refMap = {
                home: homeRef,
                atelier: atelierRef,
                collection: collectionRef,
                editorial: editorialRef,
                heritage: heritageRef,
              };
              return (
                <button 
                  key={item.key}
                  onClick={(e) => scrollToSection(refMap[item.section], item.section, e)}
                  className={`text-sm py-2 px-3 rounded-lg text-left ${activeSection === item.section ? "text-[#c9a96e] bg-[#c9a96e]/10" : "text-white/60"}`}
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={item.key} 
                    value={item.label} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage={item.section} 
                  />
                </button>
              );
            })}
          </div>
        )}
      </nav>
      
      {/* HOME SECTION - Full Screen Hero with Editable Image - FIXED SCROLLING */}
      <section ref={homeRef} className="relative min-h-screen w-full overflow-hidden">
        {/* Hero Background - Now using relative positioning */}
        <div className="absolute inset-0 w-full h-full">
          <div 
            className={`w-full h-full transition-all duration-1000 ease-out ${
              imageLoaded 
                ? "opacity-100 scale-100" 
                : "opacity-0 scale-105"
            }`}
            style={{
              transitionTimingFunction: "cubic-bezier(0.34, 1.2, 0.64, 1)",
            }}
          >
            {/* Editable Hero Image */}
            <div className="relative w-full h-full bg-[#0a0a1a]">
              <InlineEditableImage p={p} field={gownModelImageConfig.field} className="block h-full w-full overflow-hidden">
                <img
                  src={p.storeTheme?.heroImage || gownModelImageConfig.src}
                  alt="Elegant Couture Gown Model"
                  className="w-full h-full object-cover object-center object-[center_20%]"
                  loading="eager"
                  referrerPolicy="no-referrer"
                />
              </InlineEditableImage>
            </div>
            
            {/* Soft Gradient Overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a1a]/80 via-[#0a0a1a]/40 to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a1a] via-transparent to-transparent pointer-events-none" />
          </div>
          
          {/* Floating Gown Silhouettes Animation */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute text-[#c9a96e]/10 animate-gown-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  fontSize: `${30 + Math.random() * 70}px`,
                  animationDelay: `${Math.random() * 8}s`,
                  animationDuration: `${10 + Math.random() * 10}s`,
                }}
              >
                👗
              </div>
            ))}
          </div>
        </div>
        
        {/* Hero Content Overlay */}
        <div className="relative z-10 flex items-center min-h-screen">
          <div className="max-w-7xl mx-auto px-6 w-full py-20">
            <div className="max-w-2xl">
              <div className={`animate-crown-gentle mb-6 transition-all duration-700 delay-200 ${imageLoaded ? "opacity-100" : "opacity-0"}`}>
                <span className="text-5xl">👑</span>
              </div>
              
              <div className={`inline-block px-4 py-1 border-l-3 border-[#c9a96e] mb-5 transition-all duration-700 delay-300 ${imageLoaded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
                <span className="text-[#c9a96e] text-xs tracking-[0.35em] uppercase font-light">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroEyebrow" 
                    value={copy.eyebrow} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="home" 
                  />
                </span>
              </div>
              
              <h1 className={`text-5xl md:text-6xl lg:text-7xl font-light text-white leading-[1.15] tracking-wide transition-all duration-700 delay-400 ${imageLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <InlineEditableTextWithNav 
                  p={p} 
                  field="heroTitle" 
                  value={copy.title} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </h1>
              
              <p className={`mt-6 text-white/50 text-base max-w-md font-light leading-relaxed transition-all duration-700 delay-600 ${imageLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <InlineEditableTextWithNav 
                  p={p} 
                  field="heroSubtitle" 
                  value={copy.subtitle} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </p>
              
              <div className={`mt-10 flex gap-4 transition-all duration-700 delay-700 ${imageLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <button 
                  onClick={(e) => scrollToSection(collectionRef, "collection", e)}
                  className="group px-10 py-4 rounded-full bg-[#c9a96e] text-[#0a0a1a] font-semibold tracking-wide transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-[#c9a96e]/20 flex items-center gap-2"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="primaryCtaText" 
                    value={copy.primaryCta} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="collection" 
                  />
                  <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                </button>
                <button 
                  onClick={(e) => scrollToSection(heritageRef, "heritage", e)}
                  className="px-10 py-4 rounded-full border border-white/25 text-white font-semibold tracking-wide transition-all duration-500 hover:border-[#c9a96e] hover:bg-white/5 backdrop-blur-sm"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="secondaryCtaText" 
                    value={copy.secondaryCta} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="heritage" 
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Elegant Scroll Indicator */}
        <div className={`absolute bottom-10 left-1/2 -translate-x-1/2 z-10 transition-all duration-700 delay-800 ${imageLoaded ? "opacity-100" : "opacity-0"}`}>
          <div className="flex flex-col items-center gap-2">
            <span className="text-white/30 text-[9px] tracking-[0.3em]">
              <InlineEditableTextWithNav 
                p={p} 
                field="scroll_text" 
                value="EXPLORE" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="home" 
              />
            </span>
            <div className="w-5 h-9 border border-white/15 rounded-full flex justify-center">
              <div className="w-0.5 h-2 bg-[#c9a96e] rounded-full mt-2 animate-scroll-elegant" />
            </div>
          </div>
        </div>
      </section>
      
      {/* ATELIER SECTION */}
      <section ref={atelierRef} className="relative z-10 bg-[#0a0a1a] rounded-t-3xl shadow-2xl scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-4 mb-4">
              <div className="w-12 h-px bg-[#c9a96e]/40" />
              <span className="text-[#c9a96e] text-xs tracking-[0.35em] uppercase font-light">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="atelier_badge" 
                  value="Our Atelier" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="atelier" 
                />
              </span>
              <div className="w-12 h-px bg-[#c9a96e]/40" />
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-white">
              <InlineEditableTextWithNav 
                p={p} 
                field="atelier_title" 
                value="The Art of Creation" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="atelier" 
              />
            </h2>
            <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#c9a96e] to-transparent mx-auto mt-5" />
            <p className="text-white/40 text-sm mt-5 max-w-2xl mx-auto font-light">
              <InlineEditableTextWithNav 
                p={p} 
                field="atelier_subtitle" 
                value="Located in the heart of Paris, our atelier is where dreams are transformed into exquisite gowns" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="atelier" 
              />
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-[#c9a96e]/10 flex items-center justify-center">
                  <span className="text-[#c9a96e] text-xl">👗</span>
                </div>
                <div>
                  <h3 className="text-white text-xl font-light">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="atelier_feature1_title" 
                      value="Handcrafted Excellence" 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="atelier" 
                    />
                  </h3>
                  <p className="text-white/40 text-sm mt-1">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="atelier_feature1_desc" 
                      value="Each gown takes over 200 hours of meticulous work" 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="atelier" 
                    />
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-[#c9a96e]/10 flex items-center justify-center">
                  <span className="text-[#c9a96e] text-xl">✨</span>
                </div>
                <div>
                  <h3 className="text-white text-xl font-light">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="atelier_feature2_title" 
                      value="Finest Materials" 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="atelier" 
                    />
                  </h3>
                  <p className="text-white/40 text-sm mt-1">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="atelier_feature2_desc" 
                      value="Sourced from the world's best textile houses" 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="atelier" 
                    />
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-[#c9a96e]/10 flex items-center justify-center">
                  <span className="text-[#c9a96e] text-xl">👑</span>
                </div>
                <div>
                  <h3 className="text-white text-xl font-light">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="atelier_feature3_title" 
                      value="Bespoke Service" 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="atelier" 
                    />
                  </h3>
                  <p className="text-white/40 text-sm mt-1">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="atelier_feature3_desc" 
                      value="Custom fittings and personalized design" 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="atelier" 
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden">
              <InlineEditableImage p={p} field={atelierImageConfig.field} className="block w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.atelierImage || atelierImageConfig.src}
                  alt="Atelier"
                  className="w-full h-96 object-cover rounded-2xl"
                />
              </InlineEditableImage>
            </div>
          </div>
        </div>
      </section>
      
      {/* COLLECTION SECTION - REMOVED ADD TO CART */}
      <section ref={collectionRef} className="relative z-10 bg-[#0a0a1a] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-4 mb-4">
              <div className="w-12 h-px bg-[#c9a96e]/40" />
              <span className="text-[#c9a96e] text-xs tracking-[0.35em] uppercase font-light">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="The Atelier Collection" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                />
              </span>
              <div className="w-12 h-px bg-[#c9a96e]/40" />
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-white">
              <InlineEditableTextWithNav 
                p={p} 
                field="collection_title" 
                value="Haute Couture Gowns" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="collection" 
              />
            </h2>
            <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#c9a96e] to-transparent mx-auto mt-5" />
            <p className="text-white/30 text-sm mt-5 max-w-2xl mx-auto font-light">
              <InlineEditableTextWithNav 
                p={p} 
                field="collection_subtitle" 
                value="Each gown is meticulously handcrafted in our Parisian atelier using the finest silks and laces" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="collection" 
              />
            </p>
          </div>
          
          {/* Floating Gown Cards - NO ADD TO CART */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, idx) => {
              const isVisible = visibleCards.includes(idx);
              const floatDelay = getFloatingDelay(idx);
              
              return (
                <div
                  key={product._id}
                  className={`group cursor-pointer transition-all duration-700 ${
                    isVisible 
                      ? "opacity-100 translate-y-0" 
                      : "opacity-0 translate-y-24"
                  }`}
                  style={{
                    transitionTimingFunction: "cubic-bezier(0.2, 0.9, 0.4, 1.1)",
                    transitionDelay: `${idx * 0.15}s`,
                  }}
                  onMouseEnter={() => setHoveredCard(idx)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className={`relative overflow-hidden rounded-2xl bg-white/3 backdrop-blur-sm border border-white/8 hover:border-[#c9a96e]/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#c9a96e]/5 animate-float-gentle`}
                       style={{ animationDelay: `${floatDelay}s` }}>
                    
                    <div className={`absolute inset-0 bg-gradient-to-br from-[#c9a96e]/0 via-[#c9a96e]/5 to-[#c9a96e]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
                    
                    <div className="relative aspect-[3/4] overflow-hidden m-4 rounded-xl">
                      {product.productImage ? (
                        <img 
                          src={AWS_URL + product.productImage}
                          alt={product.productName}
                          className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#1a1a3a] to-[#0a0a1a] flex items-center justify-center text-7xl">👗</div>
                      )}
                      
                      <div className={`absolute inset-0 border border-[#c9a96e]/0 rounded-xl transition-all duration-500 group-hover:border-[#c9a96e]/40 group-hover:m-0`} />
                      
                      {/* View Details Overlay - NO ADD TO CART */}
                      <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center transition-all duration-500 ${
                        hoveredCard === idx ? "opacity-100" : "opacity-0"
                      }`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProduct(product);
                          }}
                          className="px-6 py-2.5 bg-[#c9a96e] text-[#0a0a1a] rounded-full text-sm font-semibold transform transition-all duration-500 hover:scale-105 hover:shadow-lg"
                        >
                          <InlineEditableTextWithNav 
                            p={p} 
                            field="view_details_button" 
                            value="View Details" 
                            onNavigate={handleNavigateAfterEdit} 
                            navPage="collection" 
                          />
                        </button>
                      </div>
                      
                      <div className="absolute top-4 left-4 bg-[#c9a96e]/90 backdrop-blur-sm text-[#0a0a1a] text-[10px] font-bold px-3 py-1 rounded-full tracking-wider">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field="couture_badge" 
                          value="COUTURE" 
                          onNavigate={handleNavigateAfterEdit} 
                          navPage="collection" 
                        />
                      </div>
                    </div>
                    
                    <div className="p-5 text-center">
                      <h3 className="font-light text-white text-lg tracking-wide">{product.productName}</h3>
                      <div className="mt-2 flex items-center justify-center gap-3">
                        <span className="text-2xl font-light text-[#c9a96e]">₹{product.salePrice}</span>
                        {product.mrp > product.salePrice && (
                          <span className="text-sm text-white/25 line-through">₹{product.mrp}</span>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="text-[#c9a96e]/70 text-xs">✦</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {visibleCards.length === products.length && products.length > 0 && (
            <div className="text-center mt-16">
              <button 
                onClick={() => p.setActiveTab("products")}
                className="group px-10 py-4 rounded-full border border-[#c9a96e]/30 text-[#c9a96e] font-light tracking-wide transition-all duration-500 hover:bg-[#c9a96e] hover:text-[#0a0a1a] inline-flex items-center gap-3"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="explore_collection_button" 
                  value="Explore Entire Collection" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                />
                <span className="group-hover:translate-x-2 transition-transform duration-300">→</span>
              </button>
            </div>
          )}
        </div>
      </section>
      
      {/* EDITORIAL SECTION */}
      <section ref={editorialRef} className="relative z-10 bg-[#0a0a1a] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative rounded-2xl overflow-hidden order-2 md:order-1">
              <InlineEditableImage p={p} field={editorialSectionImageConfig.field} className="block w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.editorialSectionImage || editorialSectionImageConfig.src}
                  alt="Editorial"
                  className="w-full h-96 object-cover rounded-2xl"
                />
              </InlineEditableImage>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-4 mb-4">
                <div className="w-12 h-px bg-[#c9a96e]/40" />
                <span className="text-[#c9a96e] text-xs tracking-[0.35em] uppercase font-light">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="editorial_badge" 
                    value="Editorial" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="editorial" 
                  />
                </span>
                <div className="w-12 h-px bg-[#c9a96e]/40" />
              </div>
              <h2 className="text-4xl md:text-5xl font-light text-white">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="editorial_title" 
                  value="The Modern Silhouette" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="editorial" 
                />
              </h2>
              <p className="text-white/40 text-sm mt-4 leading-relaxed">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="editorial_desc" 
                  value="Discover how contemporary design meets timeless elegance in our latest editorial feature. Each piece tells a unique story of craftsmanship and vision." 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="editorial" 
                />
              </p>
              <button 
                onClick={(e) => openBlogModal(editableEditorialPosts[0], e)}
                className="mt-8 px-8 py-3 rounded-full border border-[#c9a96e]/40 text-[#c9a96e] text-sm hover:bg-[#c9a96e] hover:text-[#0a0a1a] transition-all duration-500"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="read_story_button" 
                  value="Read the Story" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="editorial" 
                />
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* HERITAGE SECTION */}
      <section ref={heritageRef} className="relative z-10 bg-[#0a0a1a] scroll-mt-20">
        <div className="relative mx-6 my-16 rounded-3xl overflow-hidden group">
          <div className="absolute inset-0">
            <InlineEditableImage p={p} field={heritageImageConfig.field} className="block w-full h-full overflow-hidden">
              <img 
                src={p.storeTheme?.heritageImage || heritageImageConfig.src}
                alt="Heritage"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </InlineEditableImage>
            <div className="absolute inset-0 bg-[#0a0a1a]/75" />
          </div>
          <div className="relative px-6 py-20 text-center">
            <span className="text-[#c9a96e] text-xs tracking-[0.35em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="heritage_badge" 
                value="Our Heritage" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="heritage" 
              />
            </span>
            <h3 className="text-3xl md:text-4xl font-light text-white mt-3">
              <InlineEditableTextWithNav 
                p={p} 
                field="heritage_title" 
                value="A Legacy of Excellence" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="heritage" 
              />
            </h3>
            <p className="text-white/40 text-sm mt-3 max-w-md mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="heritage_subtitle" 
                value="For over three decades, we have been dressing the world's most elegant women" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="heritage" 
              />
            </p>
            <button 
              onClick={(e) => openBlogModal(editableEditorialPosts[1], e)}
              className="mt-8 px-8 py-3 rounded-full border border-[#c9a96e]/40 text-[#c9a96e] text-sm hover:bg-[#c9a96e] hover:text-[#0a0a1a] transition-all duration-500"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="heritage_button" 
                value="Discover Our Heritage" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="heritage" 
              />
            </button>
          </div>
        </div>
      </section>
      
      {/* Gown Style Categories */}
      <div className="relative z-10 bg-[#0a0a1a] max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {categoryItems.map((cat, idx) => (
            <div 
              key={cat.key}
              className="group text-center p-6 rounded-2xl bg-white/3 border border-white/8 hover:border-[#c9a96e]/30 transition-all duration-500 cursor-pointer hover:-translate-y-1"
              onClick={(e) => scrollToSection(collectionRef, "collection", e)}
            >
              <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-500 group-hover:rotate-2">{cat.icon}</div>
              <h3 className="text-white font-light tracking-wide">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={cat.key} 
                  value={cat.name} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                />
              </h3>
              <p className="text-white/25 text-xs mt-1">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={cat.descKey} 
                  value={cat.desc} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                />
              </p>
              <div className="w-0 h-px bg-[#c9a96e] mx-auto mt-3 group-hover:w-12 transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
      
      {/* Tabs */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-8 pb-4">
        <div className="flex justify-center gap-4 flex-wrap">
          {tabItems.map((tab) => (
            <button
              key={tab.key}
              onClick={() => p.setActiveTab(tab.key)}
              className={`px-8 py-3 rounded-full text-sm tracking-wide transition-all duration-500 ${
                p.activeTab === tab.key
                  ? "bg-[#c9a96e] text-[#0a0a1a] shadow-lg"
                  : "text-white/40 hover:text-white border border-white/10 hover:border-[#c9a96e]/30"
              }`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={tab.labelKey} 
                value={tab.label} 
                onNavigate={handleNavigateAfterEdit} 
                navPage={activeSection} 
              />
            </button>
          ))}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="relative z-10 flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <TabContent p={p} />
      </div>
      
     
      <StoreFooter p={p} dark />
      
      {/* Product Modal - NO ADD TO CART BUTTON */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-[#0a0a1a]/98 backdrop-blur-lg z-50 flex items-center justify-center p-4" onClick={() => setSelectedProduct(null)}>
          <div className="bg-gradient-to-br from-[#1a1a3a] to-[#0a0a1a] rounded-2xl max-w-md w-full overflow-hidden border border-[#c9a96e]/30 shadow-2xl animate-scale-gentle" onClick={e => e.stopPropagation()}>
            <div className="relative h-80">
              {selectedProduct.productImage ? (
                <img 
                  src={AWS_URL + selectedProduct.productImage}
                  alt={selectedProduct.productName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#1a1a3a] to-[#0a0a1a] flex items-center justify-center text-7xl">👗</div>
              )}
              <button 
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 text-white hover:bg-[#c9a96e] hover:text-black transition-all duration-300"
              >
                ✕
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <span className="text-[#c9a96e] text-[10px] tracking-wider uppercase">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="haute_couture_badge" 
                    value="Haute Couture" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="collection" 
                    isInModal={true} 
                  />
                </span>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-light text-white">{selectedProduct.productName}</h3>
              <p className="text-[#c9a96e] text-2xl font-light mt-2">₹{selectedProduct.salePrice}</p>
              
              {/* Product Description - NO ADD TO CART */}
              <p className="text-white/40 text-sm mt-4 leading-relaxed">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="product_modal_description" 
                  value={`Exquisite ${selectedProduct.productName} from our haute couture collection. Each piece is meticulously handcrafted in our Parisian atelier using the finest materials.`}
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                  isInModal={true} 
                />
              </p>
              
              <div className="mt-6 text-sm text-[#c9a96e] text-center bg-[#c9a96e]/10 p-3 rounded-full border border-[#c9a96e]/20">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="product_modal_message" 
                  value="👑 Handcrafted with love in our Parisian atelier"
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                  isInModal={true} 
                />
              </div>
              
              <button 
                onClick={() => setSelectedProduct(null)}
                className="w-full mt-6 py-3 bg-[#c9a96e] text-[#0a0a1a] rounded-full font-semibold transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="close_product_button" 
                  value="Close" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="collection" 
                  isInModal={true} 
                />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-[#0a0a1a]/98 backdrop-blur-lg z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div 
            className="bg-gradient-to-br from-[#1a1a3a] to-[#0a0a1a] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#c9a96e]/30" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-[#1a1a3a] border-b border-[#c9a96e]/20 p-4 flex justify-between items-center rounded-t-3xl">
              <h3 className="text-lg font-light text-white">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="editorial_modal_title" 
                  value="Editorial" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="editorial" 
                  isInModal={true} 
                />
              </h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-[#c9a96e]/10 text-white hover:bg-[#c9a96e] hover:text-[#0a0a1a] transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden">
                  <img src={selectedPost.image} alt="Blog" className="w-full h-64 object-cover rounded-xl" />
                </InlineEditableImage>
              </div>
              
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-[#c9a96e]/10 text-[#c9a96e] text-xs rounded-full">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="editorial_tag" 
                    value="COUTURE STORY"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />
                </span>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-light text-white mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="editorial"
                  isInModal={true}
                />
              </h2>
              
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-white/40 pb-4 border-b border-[#c9a96e]/20 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-[#c9a96e]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="editorial"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-white/70 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />
                </p>
              </div>
              
              <div className="mt-8 pt-4 border-t border-[#c9a96e]/20 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-[#c9a96e] text-[#0a0a1a] hover:bg-white transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="editorial"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes shimmer-slow {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer-slow {
          animation: shimmer-slow 4s infinite;
        }
        
        @keyframes gown-float {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
            opacity: 0;
          }
          25% {
            transform: translateY(-30px) translateX(15px);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-60px) translateX(-10px);
            opacity: 0.5;
          }
          75% {
            transform: translateY(-30px) translateX(-20px);
            opacity: 0.3;
          }
        }
        .animate-gown-float {
          animation: gown-float 15s ease-in-out infinite;
        }
        
        @keyframes crown-gentle {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-8px) scale(1.05); }
        }
        .animate-crown-gentle {
          animation: crown-gentle 4s ease-in-out infinite;
        }
        
        @keyframes scroll-elegant {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(12px); opacity: 0; }
        }
        .animate-scroll-elegant {
          animation: scroll-elegant 1.8s ease-in-out infinite;
        }
        
        @keyframes float-gentle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        .animate-float-gentle {
          animation: float-gentle 5s ease-in-out infinite;
        }
        
        @keyframes scale-gentle {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-gentle {
          animation: scale-gentle 0.3s ease-out forwards;
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        
        .scroll-mt-20 {
          scroll-margin-top: 80px;
        }
        
        .bg-white 3 {
          background-color: rgba(255, 255, 255, 0.03);
        }
        .border-white 8 {
          border-color: rgba(255, 255, 255, 0.08);
        }
      `}</style>
    </div>
  );
}